import type { TokenPair, AuthConfig } from '@core/types/api.types.ts';
import { ApiErrorCode } from '@core/types/api.types.ts';
import { AppApiError } from '@core/errors/api-errors.ts';
import { tokenStorage } from '@infra/storage/token-storage.ts';
import { RequestQueue } from './request-queue.ts';
import axios from 'axios';

export class AuthManager {
  private queue = new RequestQueue();
  private config: AuthConfig;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  getAccessToken(): string | null {
    return tokenStorage.getAccessToken();
  }

  isAuthenticated(): boolean {
    return !!tokenStorage.getAccessToken() && !tokenStorage.isTokenExpired();
  }

  setTokens(tokens: TokenPair): void {
    tokenStorage.setTokens(tokens);
  }

  logout(): void {
    tokenStorage.clearTokens();
    this.config.onAuthFailure?.();
  }

  /**
   * Refresca el access token.
   * Si ya hay un refresh en progreso, encola el request.
   */
  async refreshAccessToken(): Promise<string> {
    if (this.queue.isPending) {
      await this.queue.enqueue();
      const newToken = tokenStorage.getAccessToken();
      if (!newToken) throw this.createRefreshError();
      return newToken;
    }

    this.queue.startProcessing();

    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) throw this.createRefreshError();

      if (!this.config.refreshEndpoint) {
        throw this.createRefreshError('No refresh endpoint configured');
      }

      // Usar axios directo (sin interceptors) para evitar loops
      const response = await axios.post<TokenPair>(
        this.config.refreshEndpoint,
        { refreshToken },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10_000,
        },
      );

      const tokens = response.data;
      this.setTokens(tokens);
      this.queue.resolveAll();

      return tokens.accessToken;
    } catch {
      const apiError = this.createRefreshError();
      this.queue.rejectAll(apiError);
      this.logout();
      throw apiError;
    }
  }

  private createRefreshError(message?: string): AppApiError {
    return new AppApiError({
      code: ApiErrorCode.REFRESH_FAILED,
      message: message || 'No se pudo renovar la sesión. Inicie sesión nuevamente.',
      retryable: false,
      timestamp: new Date().toISOString(),
    });
  }
}
